// V2 shared primitives.
//
// DataTable is the workhorse: this console is data-intensive and every
// domain is a queue or a register. Sorting and filtering are client-side
// here because current volumes are small; the props are shaped so a
// server-side variant can slot in behind the same call site later.

import React, { useMemo, useState } from 'react';
import s from './ui.module.css';

export function PageHead({ title, sub, actions }) {
  return (
    <div className={s.pageHead}>
      <div className={s.pageTitles}>
        <h1 className={s.h1}>{title}</h1>
        {sub && <p className={s.sub}>{sub}</p>}
      </div>
      {actions && <div className={s.pageActions}>{actions}</div>}
    </div>
  );
}

export function Button({ primary, children, ...rest }) {
  return (
    <button type="button" className={`${s.btn} ${primary ? s.btnPrimary : ''}`} {...rest}>
      {children}
    </button>
  );
}

const TONE = { bad: s.tileBad, warn: s.tileWarn, grow: s.tileGrow };

/** `min` (px): a tighter tile for a row of many (the default is the stylesheet's 152 px). */
export function Tiles({ items, min }) {
  return (
    <div className={s.tiles} style={min ? { gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` } : undefined}>
      {items.map((t) => (
        <div key={t.label} className={`${s.tile} ${TONE[t.tone] || ''}`}>
          <span className={s.tileLabel}>{t.label}</span>
          <span className={s.tileValue}>{t.value}</span>
          {t.foot && <span className={s.tileFoot}>{t.foot}</span>}
        </div>
      ))}
    </div>
  );
}

const PILL = { bad: s.pillBad, warn: s.pillWarn, grow: s.pillGrow, pend: s.pillPend, iris: s.pillIris };
export function Pill({ tone = 'pend', children }) {
  return <span className={`${s.pill} ${PILL[tone] || s.pillPend}`}>{children}</span>;
}

export function Loading({ what = 'data' }) {
  return <div className={s.state}>Loading {what}…</div>;
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className={`${s.state} ${s.stateErr}`}>
      <p className={s.stateTitle}>Could not load this</p>
      <p style={{ margin: '0 0 12px' }}>{String(error)}</p>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function Empty({ title, children }) {
  return (
    <div className={s.state}>
      <p className={s.stateTitle}>{title}</p>
      {children}
    </div>
  );
}

/**
 * columns: [{ key, label, align?: 'num', width?, render?(row), sort?(row), wrap? }]
 * showCount: false drops the bare row count beside the filter — for a table over a server-paged register, whose counts belong to
 * the server (its chips), not to the rows this page happens to hold. Omitted, every table counts its rows as it always has.
 */
export function DataTable({ columns, rows, getKey, initialSort, filterPlaceholder, filterOn, right, showCount = true }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(initialSort || null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !filterOn) return rows;
    return rows.filter((r) =>
      filterOn.some((k) => String(r[k] ?? '').toLowerCase().includes(needle)));
  }, [rows, q, filterOn]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const val = col.sort || ((r) => r[col.key]);
    return [...filtered].sort((a, b) => {
      const A = val(a), B = val(b);
      if (A == null && B == null) return 0;
      if (A == null) return 1;
      if (B == null) return -1;
      const c = typeof A === 'number' && typeof B === 'number'
        ? A - B : String(A).localeCompare(String(B), undefined, { numeric: true });
      return sort.dir === 'desc' ? -c : c;
    });
  }, [filtered, sort, columns]);

  const toggle = (key) => setSort((cur) =>
    !cur || cur.key !== key ? { key, dir: 'asc' }
      : cur.dir === 'asc' ? { key, dir: 'desc' } : null);

  return (
    <>
      {(filterOn || showCount || right) && <div className={s.filters}>
        {filterOn && (
          <input
            className={s.input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={filterPlaceholder || 'Filter…'}
            aria-label={filterPlaceholder || 'Filter rows'}
          />
        )}
        {showCount && <span className={s.count}>
          {sorted.length.toLocaleString()}{sorted.length !== rows.length && ` of ${rows.length.toLocaleString()}`}
        </span>}
        <span className={s.spacer} />
        {right}
      </div>}

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`${c.align === 'num' ? s.num : ''} ${c.sortable === false ? '' : s.sortable}`}
                  style={c.width ? { width: c.width } : undefined}
                  onClick={c.sortable === false ? undefined : () => toggle(c.key)}
                  // A column header is where a reader asks "what IS this?", and
                  // until now there was nowhere for a table to answer.
                  title={c.title || undefined}
                  scope="col"
                >
                  {c.label}
                  {sort?.key === c.key && <span className={s.sortMark}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={getKey ? getKey(r) : i}>
                {columns.map((c) => (
                  <td key={c.key} className={`${c.align === 'num' ? s.num : ''} ${c.wrap ? s.wrapCell : ''} ${c.mono ? s.mono : ''}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? <span style={{ color: 'var(--ink-faint)' }}>—</span>)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export { s as ui };
